import React, { useState } from 'react'
import { AccountHeader } from '@mobi/component-library/Account'
import { AlertBox, Button, Icon, Input, FeatureIcon } from '@mobi/component-library/Common/V2'
import { DocumentPermission } from '@mobi/account/Areas/SignUp'
import { FormLayout, DocumentBodyStyled } from '../'
import { spacing } from '@mobi/component-library/Theme/Common'
import { statesInAustralia } from '@mobi/settings/statesInAustralia'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { ALPHANUMERIC_REGEX, getRequiredValidationMessage } from '@mobi/account/Areas/SignUp/Utils'

type DriversLicenceProps = {
  onSubmit: () => void
  isBusy: boolean
  state: string | null
  setState: (value: string) => void
  licenceNumber: string
  setLicenceNumber: (value: string) => void
  cardNumber: string
  setCardNumber: (value: string) => void
}

export const DriversLicence = ({
  onSubmit,
  isBusy,
  state,
  setState,
  licenceNumber,
  setLicenceNumber,
  cardNumber,
  setCardNumber,
}: DriversLicenceProps) => {
  const [showCardNumberInfoModal, setShowCardNumberInfoModal] = useState(false)
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})

  const allowToProceed = givePermission && !!state && !!licenceNumber && !!cardNumber

  return (
    <FormLayout>
      <AccountHeader
        alignItems='start'
        title='Enter your Driver’s Licence details'
        description='We need these details to be able to verify your identity'
        style={{ marginBottom: '0rem' }}
      />
      <DocumentBodyStyled>
        <DropdownInput
          name='state'
          label='State'
          placeholder='Details must match your ID'
          value={state || ''}
          isDisabled={isBusy}
          onChange={event => {
            setState(event.target.value || '')
          }}
          onBlur={() => {
            setValidationMessages(prev => ({ ...prev, state: getRequiredValidationMessage(state) }))
          }}
          isInvalid={!!validationMessages.state}
          helpText={validationMessages.state}
        >
          {statesInAustralia.map(state => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </DropdownInput>
        <Input
          name='licenceNumber'
          label={`Driver's licence number`}
          placeholder='Details must match your ID'
          isDisabled={isBusy}
          value={licenceNumber}
          onChange={e => setLicenceNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
          onBlur={() => {
            setValidationMessages(prev => ({
              ...prev,
              licenceNumber: getRequiredValidationMessage(licenceNumber),
            }))
          }}
          isInvalid={!!validationMessages.licenceNumber}
          helpText={validationMessages.licenceNumber}
        />
        <Input
          name='cardNumber'
          label={`Driver's licence card number`}
          placeholder='Details must match your ID'
          isDisabled={isBusy}
          value={cardNumber}
          onChange={e => setCardNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
          onBlur={() => {
            setValidationMessages(prev => ({
              ...prev,
              cardNumber: getRequiredValidationMessage(cardNumber),
            }))
          }}
          isInvalid={!!validationMessages.cardNumber}
          helpText={validationMessages.cardNumber}
          suffix={
            <Icon
              name='LineInfoCircle'
              size='2rem'
              onClick={() => setShowCardNumberInfoModal(true)}
            />
          }
        />
        <div style={{ paddingTop: spacing.smx1 }}>
          <DocumentPermission isBusy={isBusy} onChange={e => setGivePermission(e.target.checked)} />
        </div>
      </DocumentBodyStyled>
      <footer>
        <Button
          isFullWidth
          disabled={!allowToProceed}
          isLoading={isBusy}
          onClick={() => {
            onSubmit()
          }}
        >
          Continue
        </Button>
      </footer>
      <AlertBox
        title='Driver’s Licence Card Number'
        text="A 'card number' is now a mandatory verification field for Australian driver's licences. Depending on your state, this may be found on either the front or back of the card."
        icon={
          <FeatureIcon type='warning' size='lg'>
            <Icon name='LineAlertTriangle' size='2.4rem' />
          </FeatureIcon>
        }
        confirmText='Ok'
        confirmColor='primary'
        isOpen={showCardNumberInfoModal}
        onConfirm={() => setShowCardNumberInfoModal(false)}
        alignLeft
      />
    </FormLayout>
  )
}
